import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Calendar, momentLocalizer, Views } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import moment from "moment";
import "moment/locale/pl";
import axios from "axios";
import { debounce } from "lodash"; // Add lodash for debouncing

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminCalendar = () => {
  const [isBreak, setIsBreak] = useState(false); // State to track if adding a break
  const [breakNote, setBreakNote] = useState(""); // State for break note
  const [trainers, setTrainers] = useState([]); // State for trainers
  const [users, setUsers] = useState([]); // State for users
  const [filteredUsers, setFilteredUsers] = useState([]); // State for filtered users
  const [events, setEvents] = useState([]);
  const [currentView, setCurrentView] = useState(Views.DAY);
  const [modalData, setModalData] = useState(null); // Data for the modal
  const [selectedUser, setSelectedUser] = useState(""); // Selected user in the modal
  const [searchTerm, setSearchTerm] = useState(""); // State for search term
  const [currentDate, setCurrentDate] = useState(new Date()); // Track the current date
  const [selectedEvent, setSelectedEvent] = useState(null); // State for the selected event
  const [karnety, setKarnety] = useState([]); // State for karnety
  const [selectedKarnet, setSelectedKarnet] = useState(""); // Selected karnet
  const [breaks, setBreaks] = useState([]); // State for breaks
  const [firstVisits, setFirstVisits] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(moment().month()); // State for selected month
  const [selectedYear, setSelectedYear] = useState(moment().year()); // State for selected year
  const currentYear = moment().year();
  const [isUserDataLoaded, setIsUserDataLoaded] = useState(false); // Track if user data is loaded

  const [isFirstVisit, setIsFirstVisit] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [trainingType, setTrainingType] = useState("");

  const [selectedBreak, setSelectedBreak] = useState(null); // State for the selected break
  const token = localStorage.getItem("token"); // Pobierz token z localStorage

  moment.locale("pl");
  const localizer = momentLocalizer(moment);

  const trainingTypeColors = {
    "Solo EMS": "#FFA500", // Jasny pomarańczowy
    "Para EMS": "#FF8C00", // Pomarańczowy
    "Para Personalny": "#DDA0DD", // Jasny fioletowy
    "Solo Personalny": "#FF69B4", // Ciemny fioletowy
    Hybryda: "#006400", // Ciemny zielony
  };

  const dayTranslationMap = {
    niedziela: "Niedziela",
    poniedziałek: "Poniedziałek",
    wtorek: "Wtorek",
    środa: "Środa",
    czwartek: "Czwartek",
    piątek: "Piątek",
    sobota: "Sobota",
  };

  const getUnavailableHours = useCallback(
    (trainerId, selectedDate) => {
      const trainer = trainers.find((t) => t._id === trainerId);
      if (!trainer || !trainer.godziny_pracy) return [];

      const unavailableHours = [];
      const selectedDateFormatted = moment(selectedDate).format("YYYY-MM-DD");

      // Sprawdzenie wyjątkowych godzin
      const exceptionalHours = trainer.wyjatkowe_godziny_pracy?.filter(
        (ex) => moment(ex.data).format("YYYY-MM-DD") === selectedDateFormatted
      );

      if (exceptionalHours && exceptionalHours.length > 0) {
        exceptionalHours.sort((a, b) => {
          const aStart = moment(a.godzina_start, "HH:mm");
          const bStart = moment(b.godzina_start, "HH:mm");
          return aStart.diff(bStart);
        });

        // Dodaj bloki niedostępności
        unavailableHours.push({
          title: "Niedostępny",
          start: moment(selectedDate).startOf("day").toDate(),
          end: moment(selectedDate)
            .hour(exceptionalHours[0].godzina_start.split(":")[0])
            .minute(exceptionalHours[0].godzina_start.split(":")[1])
            .toDate(),
          resourceId: trainerId,
          isUnavailable: true,
        });

        // Dodaj bloki między przedziałami
        for (let i = 1; i < exceptionalHours.length; i++) {
          const prevEnd = exceptionalHours[i - 1].godzina_koniec;
          const currentStart = exceptionalHours[i].godzina_start;

          unavailableHours.push({
            title: "Niedostępny",
            start: moment(selectedDate)
              .hour(prevEnd.split(":")[0])
              .minute(prevEnd.split(":")[1])
              .toDate(),
            end: moment(selectedDate)
              .hour(currentStart.split(":")[0])
              .minute(currentStart.split(":")[1])
              .toDate(),
            resourceId: trainerId,
            isUnavailable: true,
          });
        }

        // Dodaj blok od ostatniej godziny do końca dnia
        const lastEnd =
          exceptionalHours[exceptionalHours.length - 1].godzina_koniec;
        unavailableHours.push({
          title: "Niedostępny",
          start: moment(selectedDate)
            .hour(lastEnd.split(":")[0])
            .minute(lastEnd.split(":")[1])
            .toDate(),
          end: moment(selectedDate).endOf("day").toDate(),
          resourceId: trainerId,
          isUnavailable: true,
        });

        return unavailableHours;
      }

      // Standardowe godziny pracy
      const selectedDayName = moment(selectedDate).format("dddd");
      const selectedDayNamePolish = dayTranslationMap[selectedDayName];

      const workingHours = trainer.godziny_pracy.find(
        (day) => day.dzien === selectedDayNamePolish
      );

      if (workingHours) {
        unavailableHours.push({
          title: "Niedostępny",
          start: moment(selectedDate).startOf("day").toDate(),
          end: moment(selectedDate)
            .hour(workingHours.godzina_start.split(":")[0])
            .minute(workingHours.godzina_start.split(":")[1])
            .subtract(1, "minute")
            .toDate(),
          resourceId: trainerId,
          isUnavailable: true,
        });

        unavailableHours.push({
          title: "Niedostępny",
          start: moment(selectedDate)
            .hour(workingHours.godzina_koniec.split(":")[0])
            .minute(workingHours.godzina_koniec.split(":")[1])
            .add(1, "minute")
            .toDate(),
          end: moment(selectedDate).endOf("day").toDate(),
          resourceId: trainerId,
          isUnavailable: true,
        });
      }

      return unavailableHours;
    },
    [trainers, dayTranslationMap]
  );

  // Separated fetch functions
  const fetchCalendarData = useCallback(async () => {
    try {
      const [
        trainersResponse,
        treningsResponse,
        breaksResponse,
        firstVisitsResponse,
      ] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/trenerzy/getTrenerzy`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
        axios.get(`${BACKEND_URL}/api/treningi/getTreningi`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
        axios.get(`${BACKEND_URL}/api/breaks/getAllBreaks`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
        axios.get(`${BACKEND_URL}/api/treningi/getFirstVisits`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
      ]);

      setTrainers(trainersResponse.data);

      const formattedEvents = treningsResponse.data
        .filter((event) => event.status !== "anulowany")
        .map((event) => {
          const karnet = event.karnet_id;

          const paymentsDueCount = karnet
            ? karnet.completed_trainings === 0
              ? 0
              : Math.max(
                  karnet.przypomnienie_platnosc -
                    (karnet.completed_trainings %
                      karnet.przypomnienie_platnosc) -
                    2
                )
            : 0;
          let czyWymaganaPlatnosc = false;
          let expectedInstallments = 0;
          let czyWiecej = false;
          let totalPayments = 0;
          if (karnet) {
            const reminderInterval = karnet.przypomnienie_platnosc;
            const totalInstallments = karnet.ilosc_rat;
            const completedTrainings = karnet.completed_trainings;

            expectedInstallments = Math.min(
              Math.floor((completedTrainings + 2) / reminderInterval) + 1,
              Math.ceil(karnet.cena / karnet.cena_za_rate)
            );

            totalPayments = Math.ceil(karnet.cena / karnet.cena_za_rate);

            if (totalPayments - expectedInstallments < totalInstallments) {
              czyWymaganaPlatnosc = true;
            }
            if (totalPayments - expectedInstallments >= totalInstallments) {
              czyWiecej = true;
            }
          }

          const remainingTrainings = karnet ? karnet.ilosc_wejsc : 0;

          return {
            title: `${event.uzytkownicy.imie} ${event.uzytkownicy.nazwisko}`,
            start: new Date(event.godzina_start),
            end: new Date(event.godzina_koniec),
            resourceId: event.trener_id._id,
            treningId: event._id,
            typ_treningu: event.typ_treningu,
            note: event.uzytkownicy.notatka || "",
            karnet_id: karnet,
            completed_trainings: karnet ? karnet.completed_trainings : 0,
            ilosc_rat: karnet ? karnet.ilosc_rat : null,
            cena_za_rate: karnet ? karnet.cena_za_rate : null,
            platnosc_rodzaj: karnet ? karnet.platnosc_rodzaj : null,
            karnet_oplacone: karnet ? karnet.karnet_oplacone : null,
            paymentsDueCount,
            przypomnienie_platnosc: karnet
              ? karnet.przypomnienie_platnosc
              : null,
            status: karnet ? karnet.status : null,
            czyWymaganaPlatnosc,
            czyWiecej,
            totalPayments,
            remainingTrainings,
            expectedInstallments,
          };
        });

      const formattedBreaks = breaksResponse.data.map((breakItem) => ({
        id: breakItem._id,
        title: "Przerwa",
        start: new Date(breakItem.start),
        end: new Date(breakItem.end),
        resourceId: breakItem.trainerId._id,
        note: breakItem.note || "",
      }));

      const formattedFirstVisits = firstVisitsResponse.data.map((visit) => ({
        title: `Próbny Trening: ${visit.firstName} ${visit.lastName}`,
        start: new Date(visit.godzina_start),
        end: new Date(visit.godzina_koniec),
        resourceId: visit.trener_id,
        typ_treningu: visit.typ_treningu,
        isFirstVisit: true,
        visitData: visit,
      }));

      setBreaks(formattedBreaks);
      const allEvents = [...formattedEvents, ...formattedFirstVisits];

      // Get unavailable hours for all trainers
      const unavailableEvents = trainersResponse.data.flatMap((trainer) =>
        getUnavailableHours(trainer._id, currentDate)
      );

      // Set events with a slight delay to ensure trainers data is processed
      setTimeout(() => {
        setEvents([...allEvents, ...unavailableEvents]);
      }, 0);

      setFirstVisits(firstVisitsResponse);
    } catch (error) {
      console.error("Error fetching calendar data:", error);
    }
  }, [currentDate, token, getUnavailableHours]);

  // Function to load user data only when needed
  const loadUserData = useCallback(async () => {
    if (isUserDataLoaded) return; // Only fetch if not already loaded

    try {
      const usersResponse = await axios.get(
        `${BACKEND_URL}/api/user/getAllUsers`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setUsers(usersResponse.data);
      setFilteredUsers(usersResponse.data);
      setIsUserDataLoaded(true);
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  }, [token, isUserDataLoaded]);

  // Create debounced version of fetchCalendarData
  const debouncedFetchCalendarData = useMemo(
    () => debounce(fetchCalendarData, 300),
    [fetchCalendarData]
  );

  const handleSaveFirstVisit = async () => {
    if (
      !modalData ||
      !firstName ||
      !lastName ||
      !email ||
      !phone ||
      !trainingType
    ) {
      alert("Proszę wypełnić wszystkie pola.");
      return;
    }

    const requestData = {
      firstName: firstName,
      lastName: lastName,
      email: email,
      phone: phone,
      trainingType: trainingType,
      selectedDate: modalData.start.toISOString().split("T")[0], // Use modalData.start for the date
      selectedTime: moment(modalData.start).format("HH:mm"), // Zmiana na format HH:mm
      trainerId: modalData.resourceId, // Use resourceId from modalData
    };

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/treningi/addFirstVisit`,
        requestData,
        {
          headers: {
            Authorization: `Bearer ${token}`, // Dodaj token do nagłówka
          },
        }
      );
      setIsFirstVisit(false);
      setModalData(null);
      fetchCalendarData(); // Refresh the data
    } catch (error) {
      console.error("Error adding first visit:", error);
      alert("Wystąpił błąd podczas dodawania pierwszej wizyty.");
    }
  };

  const handleMonthChange = (event) => {
    setSelectedMonth(event.target.value);
    setCurrentDate(
      moment()
        .year(selectedYear)
        .month(event.target.value)
        .startOf("month")
        .toDate()
    );
  };

  const handleYearChange = (event) => {
    setSelectedYear(event.target.value);
    setCurrentDate(
      moment()
        .year(event.target.value)
        .month(selectedMonth)
        .startOf("month")
        .toDate()
    );
  };

  const Star = React.memo(({ filled }) => (
    <span style={{ color: filled ? "gold" : "lightgray", fontSize: "1.2em" }}>
      ★
    </span>
  ));

  const isOverlapping = (newEvent, existingEvents) => {
    return existingEvents.some((event) => {
      return (
        event.resourceId === newEvent.resourceId && // Same trainer
        ((newEvent.start >= event.start && newEvent.start < event.end) || // New start is within existing event
          (newEvent.end > event.start && newEvent.end <= event.end) || // New end is within existing event
          (newEvent.start <= event.start && newEvent.end >= event.end)) // New event encompasses existing event
      );
    });
  };

  // Only fetch calendar data (without users) on initial load and date change
  useEffect(() => {
    debouncedFetchCalendarData();
    return () => debouncedFetchCalendarData.cancel(); // Cancel debounce on unmount
  }, [debouncedFetchCalendarData]);

  const filteredUsersMemo = useMemo(() => {
    return users.filter(
      (user) =>
        user.imie.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.nazwisko.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, users]);

  const handleSelectSlot = useCallback(
    ({ start, end, resourceId }) => {
      if (modalData) {
        return;
      }
      if (currentView !== Views.DAY) {
        setCurrentDate(start);
        setCurrentView(Views.DAY);
        return;
      }

      if (isBreak) {
        setModalData({ start, end, resourceId });
      } else {
        // Load user data when opening modal
        loadUserData();
        setModalData({ start, end, resourceId });
      }
    },
    [modalData, currentView, isBreak, loadUserData]
  );

  const handleSelectEvent = useCallback((event) => {
    if (event.title === "Przerwa") {
      setSelectedBreak(event); // Set the selected break
    } else if (event.isFirstVisit) {
      // Show different modal for first visits
      setSelectedEvent({
        ...event,
        isFirstVisit: true,
        visitData: event.visitData,
      });
    } else if (event.isUnavailable) {
      // do nothing for unavailable slots
    } else {
      setSelectedEvent(event);
    }
  }, []);

  const getAvailableTrainers = (selectedDate) => {
    return trainers.filter((trainer) => {
      const selectedDateFormatted = moment(selectedDate).format("YYYY-MM-DD");
      const selectedDayName = moment(selectedDate).format("dddd");
      const selectedDayNamePolish = dayTranslationMap[selectedDayName];

      // Sprawdzenie wyjątkowych godzin pracy dla wybranego dnia
      const exceptionalHours = trainer.wyjatkowe_godziny_pracy?.find(
        (ex) => moment(ex.data).format("YYYY-MM-DD") === selectedDateFormatted
      );

      if (exceptionalHours) {
        return true; // Jeśli są wyjątkowe godziny, trener jest dostępny
      }

      // Sprawdzenie standardowych godzin pracy
      const workingHours = trainer.godziny_pracy.find(
        (day) => day.dzien === selectedDayNamePolish
      );

      if (!workingHours) return false; // Jeśli brak wpisu, trener nie pracuje tego dnia

      // Jeśli godziny pracy to 09:00-09:00 i nie ma wyjątkowych godzin, trener jest niedostępny
      return !(workingHours.godzina_start === workingHours.godzina_koniec);
    });
  };

  const handleSaveTraining = () => {
    if (!selectedUser || !modalData || !selectedKarnet) {
      alert("Proszę wybrać użytkownika i karnet przed zapisaniem treningu.");
      return;
    }
    if (selectedKarnet.ilosc_wejsc_kal <= 0) {
      alert("Brak dostępnych wejść na karnecie.");
      return;
    }

    const trainer = trainers.find((t) => t._id === modalData.resourceId);
    const user = users.find((u) => u._id === selectedUser);

    if (trainer && user) {
      // Check if the trainer offers the selected training type
      if (!trainer.typy_treningow.includes(selectedKarnet.rodzaj)) {
        alert(
          `Trener ${trainer.imie} ${trainer.nazwisko} nie prowadzi treningu typu ${selectedKarnet.rodzaj}.`
        );
        return;
      }

      // Define allowed durations in minutes
      const trainingDurationLimits = {
        "Solo EMS": 30,
        "Para EMS": 30,
        "Solo Personalny": 60,
        "Para Personalny": 60,
        Hybryda: 60,
      };

      // Calculate the duration of the training session
      const durationInMinutes = (modalData.end - modalData.start) / (1000 * 60); // Convert milliseconds to minutes

      // Check if the training type has a duration limit
      const allowedDuration = trainingDurationLimits[selectedKarnet.rodzaj];

      // Check for specific training types that require a strict duration
      if (
        selectedKarnet.rodzaj === "Solo Personalny" ||
        selectedKarnet.rodzaj === "Para Personalny"
      ) {
        if (durationInMinutes !== 60) {
          alert(
            `Trening ${selectedKarnet.rodzaj} musi trwać dokładnie 60 minut.`
          );
          return;
        }
      } else if (selectedKarnet.rodzaj === "Hybryda") {
        if (durationInMinutes !== 30 && durationInMinutes !== 60) {
          alert(`Trening Hybryda musi trwać dokładnie 30 lub 60 minut.`);
          return;
        }
      } else if (allowedDuration && durationInMinutes > allowedDuration) {
        alert(
          `Trening ${selectedKarnet.rodzaj} może trwać maksymalnie ${allowedDuration} minut.`
        );
        return;
      }

      // Check for Para EMS collision
      if (selectedKarnet.rodzaj === "Para EMS") {
        const sameTimeEvents = events.filter((event) => {
          if (
            event.isUnavailable ||
            event.title === "Przerwa" ||
            event.isFirstVisit
          )
            return false;

          const eventDate = moment(event.start).format("YYYY-MM-DD");
          const newEventDate = moment(modalData.start).format("YYYY-MM-DD");

          if (eventDate !== newEventDate) return false;

          return (
            (modalData.start >= event.start && modalData.start < event.end) ||
            (modalData.end > event.start && modalData.end <= event.end) ||
            (modalData.start <= event.start && modalData.end >= event.end)
          );
        });

        const paraEmsCount = sameTimeEvents.filter(
          (event) => event.typ_treningu === "Para EMS"
        ).length;

        if (paraEmsCount >= 1) {
          alert(
            "Kolizja PAR! Nie można umówić dwóch par EMS na tę samą godzinę."
          );
          return;
        }
      }

      const newEvent = {
        title: `Trening z ${user.imie + " " + user.nazwisko}`,
        start: modalData.start,
        end: modalData.end,
        resourceId: modalData.resourceId,
        typ_treningu: selectedKarnet.rodzaj,
        note: user.notatka || "",
        karnet_id: selectedKarnet._id,
      };

      // Send the event to the backend
      axios
        .post(
          `${BACKEND_URL}/api/treningi/addTreningi`,
          {
            trener_id: modalData.resourceId,
            uzytkownicy: selectedUser,
            karnet_id: selectedKarnet._id,
            data: modalData.start,
            godzina_start: modalData.start,
            godzina_koniec: modalData.end,
            typ_treningu: selectedKarnet.rodzaj,
            bookedBy: "admin",
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then(() => {
          // Fetch updated data after adding training
          fetchCalendarData();
          setModalData(null);
          setSelectedUser("");
          setSelectedKarnet(null); // Reset selected karnet
        })
        .catch((error) => {
          console.error("Error adding training:", error);
          alert("Wystąpił błąd podczas dodawania treningu.");
        });
      axios
        .put(
          `${BACKEND_URL}/api/karnet/updateKarnet/${selectedKarnet._id}`,
          {
            ilosc_wejsc_kal: selectedKarnet.ilosc_wejsc_kal - 1,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((karnetUpdateResponse) => {})
        .catch((error) => {});
    }
  };

  const handleSaveBreak = () => {
    if (!modalData) {
      alert("Wybierz czas przerwy.");
      return;
    }

    const newBreak = {
      trainerId: modalData.resourceId, // Use trainerId instead of resourceId
      start: modalData.start, // Ensure this is a Date object
      end: modalData.end, // Ensure this is a Date object
      note: breakNote, // Include the note
    };

    const newBreakk = {
      title: `Przerwa`,
      trainerId: modalData.resourceId, // Use trainerId instead of resourceId
      start: modalData.start, // Ensure this is a Date object
      end: modalData.end, // Ensure this is a Date object
      note: breakNote || "", // Include the note
    };
    // Send the new break to the backend
    axios
      .post(`${BACKEND_URL}/api/breaks/addBreak`, newBreak, {
        headers: {
          Authorization: `Bearer ${token}`, // Dodaj token do nagłówka
        },
      })

      .then((response) => {
        // Update the state with the new break
        setBreaks((prev) => [...prev, newBreakk]); // Assuming response.data contains the created break
        setModalData(null); // Close the modal
        setBreakNote(""); // Reset break note
        fetchCalendarData(); // Fetch data again after adding a break
      })
      .catch((error) => {
        console.error("Error adding break:", error.message);
        alert("Wystąpił błąd podczas dodawania przerwy.");
      });
  };

  const handleCancel = () => {
    setModalData(null);
    setSelectedUser("");
  };

  const handleCancelTraining = () => {
    if (!selectedEvent) return;

    // Update the training session status to "anulowany"
    axios.put(
      `${BACKEND_URL}/api/treningi/updateTrening/${selectedEvent.treningId}`,
      {
        status: "anulowany", // Change status to "anulowany"
      },
      {
        headers: {
          Authorization: `Bearer ${token}`, // Dodaj token do nagłówka
        },
      }
    );

    // Now fetch the karnet by ID to update the number of entries

    // Update the karnet to add back the number of entries
    return axios
      .put(
        `${BACKEND_URL}/api/karnet/updateKarnet/${selectedEvent.karnet_id._id}`,
        {
          ilosc_wejsc_kal: selectedEvent.karnet_id.ilosc_wejsc_kal + 1, // Add back one entry
        },
        {
          headers: {
            Authorization: `Bearer ${token}`, // Dodaj token do nagłówka
          },
        }
      )

      .then((karnetUpdateResponse) => {
        // Remove the training session from the state
        setEvents((prev) =>
          prev.filter(
            (event) =>
              event.start !== selectedEvent.start &&
              event.end !== selectedEvent.end
          )
        );
        setSelectedEvent(null); // Clear the selected event
      })
      .catch((error) => {
        console.error("Error updating karnet:", error.message);
      })

      .catch((error) => {
        console.error("Error updating training status:", error.message);
      });
  };

  const handleCancelFirstVisit = async () => {
    if (!selectedEvent || !selectedEvent.isFirstVisit) return;

    try {
      await axios.delete(
        `${BACKEND_URL}/api/treningi/deleteFirstVisit/${selectedEvent.visitData._id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`, // Dodaj token do nagłówka
          },
        }
      );
      // Remove the first visit from the state
      setEvents((prev) =>
        prev.filter(
          (event) =>
            event.start !== selectedEvent.start &&
            event.end !== selectedEvent.end
        )
      );
      setSelectedEvent(null); // Clear the selected event
    } catch (error) {
      console.error("Error canceling first visit:", error);
      alert("Wystąpił błąd podczas anulowania pierwszej wizyty.");
    }
  };

  // Optimize to only fetch karnety for a specific user when needed
  const fetchKarnety = async (userId) => {
    try {
      if (!userId) {
        console.error("User ID is missing.");
        return;
      }

      const response = await axios.get(
        `${BACKEND_URL}/api/karnet/getKarnetByUserId/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setKarnety(response.data);
    } catch (error) {
      console.error("Error fetching karnety:", error.message);
    }
  };

  const handleCancelBreak = async () => {
    if (!selectedBreak) return;

    try {
      await axios.delete(
        `${BACKEND_URL}/api/breaks/deleteBreak/${selectedBreak.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`, // Dodaj token do nagłówka
          },
        }
      );
      // Remove the break from the state
      setBreaks((prev) =>
        prev.filter((breakItem) => breakItem.start !== selectedBreak.start)
      );
      setSelectedBreak(null); // Clear the selected break
    } catch (error) {
      console.error("Error canceling break:", error);
      alert("Wystąpił błąd podczas anulowania przerwy.");
    }
  };

  const isPersonalTrainingAtSameTime = (newEvent, existingEvents) => {
    const eventDate = moment(newEvent.start).format("YYYY-MM-DD");

    // Filtrujemy wydarzenia tylko dla tego samego dnia
    const sameDayEvents = existingEvents.filter(
      (event) => moment(event.start).format("YYYY-MM-DD") === eventDate
    );

    const sameTimeEvents = sameDayEvents.filter(
      (event) =>
        (newEvent.start >= event.start && newEvent.start < event.end) ||
        (newEvent.end > event.start && newEvent.end <= event.end) ||
        (newEvent.start <= event.start && newEvent.end >= event.end)
    );

    const hasPersonalTraining = sameTimeEvents.some(
      (event) =>
        event.typ_treningu === "Solo Personalny" ||
        event.typ_treningu === "Para Personalny"
    );

    const pairEmsCount = sameTimeEvents.filter(
      (event) => event.typ_treningu === "Para EMS"
    ).length;

    // Jeśli istnieje trening personalny, pozwalamy tylko na jedną parę EMS
    if (hasPersonalTraining && pairEmsCount >= 1) {
      return false;
    }

    return true;
  };

  return (
    <div className="p-4 bg-gray-50 min-h-screen">
      <h1 className="text-center text-3xl font-bold mb-6 text-gray-800">
        Kalendarz Treningów
      </h1>

      {/* Informacja o aktualnym wyborze */}
      <h2 className="text-center text-xl mb-4 text-gray-700">
        {isBreak ? "Wybierasz obecnie przerwę" : "Wybierasz obecnie trening"}
      </h2>

      {/* Dropdown for selecting month */}
      <select
        value={selectedMonth}
        onChange={handleMonthChange}
        className="mb-4 p-2 border rounded-lg"
      >
        {Array.from({ length: 12 }, (_, index) => (
          <option key={index} value={index}>
            {moment().month(index).format("MMMM")}
          </option>
        ))}
      </select>

      {/* Dropdown for selecting year */}
      <select
        value={selectedYear}
        onChange={handleYearChange}
        className="mb-4 p-2 border rounded-lg"
      >
        {Array.from({ length: 6 }, (_, index) => (
          <option key={index} value={currentYear - 5 + index}>
            {currentYear - 5 + index}
          </option>
        ))}
      </select>

      <div className="flex justify-between mb-6 gap-4">
        <button
          className={`px-6 py-2 ${
            isBreak ? "bg-orange-500" : "bg-green-500"
          } hover:bg-green-500 text-white rounded-lg transition duration-200 shadow-md`}
          onClick={() => setIsBreak(true)}
        >
          Dodaj Przerwę
        </button>
        <button
          className={`px-6 py-2 ${
            !isBreak ? "bg-orange-500" : "bg-blue-500"
          } hover:bg-blue-500 text-white rounded-lg transition duration-200 shadow-md`}
          onClick={() => setIsBreak(false)}
        >
          Dodaj Trening
        </button>
        <button
          className={`px-6 py-2 ${
            isFirstVisit ? "bg-purple-500" : "bg-purple-500"
          } hover:bg-purple-600 text-white rounded-lg transition duration-200 shadow-md`}
          onClick={() => {
            setIsFirstVisit(true);
            loadUserData(); // Load user data when needed
          }}
        >
          Dodaj Próbny Trening
        </button>
      </div>
      <div className="bg-white rounded-lg shadow-lg p-4">
        <Calendar
          localizer={localizer}
          events={[...events, ...breaks]}
          date={currentDate}
          startAccessor="start"
          endAccessor="end"
          view={currentView}
          views={[Views.DAY, Views.MONTH]}
          onNavigate={(date) => {
            setCurrentDate(date);
          }}
          step={30}
          timeslots={1}
          selectable
          resources={getAvailableTrainers(currentDate).map((trainer) => ({
            id: trainer._id,
            name: `${trainer.imie} ${trainer.nazwisko}`,
          }))}
          resourceIdAccessor="id"
          resourceTitleAccessor="name"
          onSelectSlot={handleSelectSlot}
          onView={setCurrentView}
          style={{ height: 700 }}
          onSelectEvent={handleSelectEvent}
          culture="pl"
          formats={{
            timeGutterFormat: "HH:mm",
            eventTimeRangeFormat: ({ start, end }) =>
              `${moment(start).format("HH:mm")} - ${moment(end).format(
                "HH:mm"
              )}`,
          }}
          eventPropGetter={(event) => {
            let backgroundColor;

            if (event.isUnavailable) {
              backgroundColor = "#808080"; // Red for unavailable hours
            } else if (event.title === "Przerwa") {
              backgroundColor = "#a6a6a6";
            } else if (event.isFirstVisit) {
              if (
                event.typ_treningu ===
                "Konsultacja + trening personalny EMS + drenaż limfatyczny za 69 zł"
              ) {
                backgroundColor = "#FF1493"; // Różowy
              } else if (
                event.typ_treningu ===
                "Konsultacja + trening personalny siłowy (bez kombinezonu EMS) za 69 zł"
              ) {
                backgroundColor = "#1E90FF"; // Niebieski
              } else if (
                event.typ_treningu ===
                "Pierwsza wizyta Program Hybrydowy (EMS + siłowy) za 89 zł"
              ) {
                backgroundColor = "#32CD32"; // Zielony
              } else {
                backgroundColor = "#9932CC"; // Fioletowy dla innych pierwszych wizyt
              }
            } else {
              backgroundColor =
                trainingTypeColors[event.typ_treningu] || "#4682b4";
            }

            return {
              style: {
                backgroundColor:
                  event.karnet_id &&
                  event.platnosc_rodzaj == "rata" &&
                  (event.paymentsDueCount === 0 || event.czyWymaganaPlatnosc) &&
                  event.ilosc_rat > 0 &&
                  !event.czyWiecej
                    ? "rgb(239, 68, 68)" // red-500
                    : backgroundColor,
                color: "white",
                borderRadius: "5px",
                padding: "10px",
                margin: "5px",
                transition: "background-color 0.3s",
              },
            };
          }}
          messages={{
            today: "Dziś",
            previous: "Poprzedni",
            next: "Następny",
            month: "Miesiąc",
            week: "Tydzień",
            day: "Dzień",
            agenda: "Agenda",
            date: "Data",
            time: "Czas",
            event: "Wydarzenie",
            noEventsInRange: "Brak wydarzeń w tym okresie",
          }}
          min={new Date(1970, 1, 1, 6, 0, 0)}
        />
      </div>

      {/* Modal */}
      {modalData && !isBreak && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex justify-center items-center p-4 z-50">
          <div className="bg-white p-8 rounded-xl shadow-2xl max-w-md w-full">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">
              Nowy Trening
            </h2>
            <p className="mb-2">
              Trener:{" "}
              {trainers.find((t) => t._id === modalData.resourceId)?.imie ||
                "Nieznany"}
            </p>
            <p className="mb-2">
              Godzina: {moment(modalData.start).format("HH:mm")} -{" "}
              {moment(modalData.end).format("HH:mm")}
            </p>
            <label className="block mb-2 font-semibold">
              Wybierz użytkownika:
            </label>
            <input
              type="text"
              className="w-full p-2 border rounded-lg mb-4"
              placeholder="Szukaj użytkownika..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
            <ul className="border rounded-lg max-h-40 overflow-y-auto">
              {filteredUsersMemo.map((user) => (
                <li
                  key={user._id}
                  className={`p-2 hover:bg-gray-200 cursor-pointer ${
                    selectedUser === user._id ? "bg-blue-200" : ""
                  }`}
                  onClick={() => {
                    setSelectedUser(user._id);
                    setSelectedKarnet(null); // Reset selected membership
                    fetchKarnety(user._id);
                  }}
                >
                  {user.imie + " " + user.nazwisko}
                </li>
              ))}
            </ul>
            <label className="block mb-2 font-semibold">Wybierz karnet:</label>
            <select
              className="w-full p-2 border rounded-lg mb-4"
              value={selectedKarnet ? selectedKarnet._id : ""}
              onChange={(e) => {
                const karnet = karnety.find((k) => k._id === e.target.value);
                if (karnet) {
                  setSelectedKarnet(karnet);
                } else {
                  console.error("Karnet not found.");
                }
              }}
            >
              <option value="">-- Wybierz karnet --</option>
              {karnety
                .filter((karnet) => karnet.ilosc_wejsc_kal > 0)
                .map((karnet) => (
                  <option key={karnet._id} value={karnet._id}>
                    {karnet.nazwa} (Pozostało wejść na zapisy:{" "}
                    {karnet.ilosc_wejsc_kal})
                    {!karnet.aktywny && " (Nieaktywny)"}
                  </option>
                ))}
            </select>

            {/* Display the number of entries for the selected karnet */}
            {selectedKarnet && (
              <div className="flex flex-col">
                <p className="mb-2">
                  Ilość Wejść: {selectedKarnet.ilosc_wejsc}
                </p>
                <p className="mb-2">
                  Ilość wejść na zapisy: {selectedKarnet.ilosc_wejsc_kal}
                </p>
              </div>
            )}

            <div className="flex justify-end gap-4 mt-6">
              <button
                className="px-6 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition duration-200"
                onClick={handleCancel}
              >
                Anuluj
              </button>
              <button
                className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition duration-200"
                onClick={handleSaveTraining}
              >
                Zapisz
              </button>
            </div>
          </div>
        </div>
      )}
      {modalData && isBreak && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex justify-center items-center p-4 z-50">
          <div className="bg-white p-8 rounded-xl shadow-2xl max-w-md w-full">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">
              Nowa Przerwa
            </h2>
            <p className="mb-2">
              Trener:{" "}
              {trainers.find((t) => t._id === modalData.resourceId)?.imie ||
                "Nieznany"}
            </p>
            <p className="mb-2">
              Godzina: {moment(modalData.start).format("HH:mm")} -{" "}
              {moment(modalData.end).format("HH:mm")}
            </p>
            <label className="block mb-2 font-semibold">Notatka:</label>
            <input
              type="text"
              className="w-full p-2 border rounded-lg mb-4"
              placeholder="Wpisz notatkę..."
              value={breakNote}
              onChange={(e) => setBreakNote(e.target.value)}
            />
            <div className="flex justify-end gap-4 mt-6">
              <button
                className="px-6 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition duration-200"
                onClick={handleCancel}
              >
                Anuluj
              </button>
              <button
                className="px-6 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition duration-200"
                onClick={handleSaveBreak}
              >
                Zapisz Przerwę
              </button>
            </div>
          </div>
        </div>
      )}
      {selectedEvent && !selectedEvent.isFirstVisit && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h2 className="text-lg font-bold mb-4">Szczegóły Treningu</h2>
            <p className="mb-2">Użytkownik: {selectedEvent.title}</p>
            <p className="mb-2">Typ Treningu: {selectedEvent.typ_treningu}</p>
            <p className="mb-2">Notatka: {selectedEvent.note}</p>

            <p className="mb-2">
              <strong>Pozostało:</strong> {selectedEvent.remainingTrainings}{" "}
              Trening do konca karnetu
            </p>
            <p className="mb-2">
              <strong>Wykonanych treningów:</strong>{" "}
              {selectedEvent.completed_trainings}{" "}
            </p>
            {selectedEvent.platnosc_rodzaj === "rata" ? (
              <>
                {" "}
                <p className="mb-2">
                  <strong>Płatność za:</strong>{" "}
                  {selectedEvent.paymentsDueCount === -1 && (
                    <span>
                      {selectedEvent.przypomnienie_platnosc - 1} Trening
                    </span>
                  )}
                  {selectedEvent.paymentsDueCount === -2 && (
                    <span>
                      {selectedEvent.przypomnienie_platnosc - 2} Trening
                    </span>
                  )}
                  {selectedEvent.paymentsDueCount !== -1 &&
                    selectedEvent.paymentsDueCount !== -2 && (
                      <>{selectedEvent.paymentsDueCount} Trening</>
                    )}
                </p>
                <p className="mb-2">
                  <strong>Ilosć Rat:</strong> {selectedEvent.ilosc_rat}
                </p>
                <p className="mb-2">
                  <strong>Cena za Ratę:</strong> {selectedEvent.cena_za_rate}{" "}
                  PLN
                </p>
                <strong> Całość:</strong> {selectedEvent.karnet_id.cena} PLN
              </>
            ) : (
              <p className="mb-2">
                <strong>Karnet opłacony, Całość:</strong>{" "}
                {selectedEvent.karnet_id.cena} PLN
              </p>
            )}
            {/* Display stars for the selected karnet */}
            {selectedEvent.karnet_id && (
              <div className="mb-2">
                <span className="font-semibold">Gwiazdy: </span>
                {Array.from(
                  { length: selectedEvent.karnet_id.max_gwiazdek },
                  (_, index) => (
                    <Star
                      key={index}
                      filled={index < selectedEvent.karnet_id.ilosc_gwiazdek}
                    />
                  )
                )}
              </div>
            )}

            <div className="flex justify-end gap-4">
              <button
                className="px-4 py-2 bg-gray-300 rounded-lg"
                onClick={() => setSelectedEvent(null)}
              >
                Zamknij
              </button>
              <button
                className="px-4 py-2 bg-red-500 text-white rounded-lg"
                onClick={handleCancelTraining}
              >
                Anuluj Trening
              </button>
            </div>
          </div>
        </div>
      )}
      {selectedEvent && selectedEvent.isFirstVisit && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h2 className="text-lg font-bold mb-4">
              Szczegóły Próbnego treningu
            </h2>
            <p className="mb-2">
              Imię i Nazwisko: {selectedEvent.visitData.firstName}{" "}
              {selectedEvent.visitData.lastName}
            </p>
            <p className="mb-2">Telefon: {selectedEvent.visitData.phone}</p>
            <p className="mb-2">Email: {selectedEvent.visitData.email}</p>
            <p className="mb-2">
              Typ Treningu: {selectedEvent.visitData.typ_treningu}
            </p>
            <div className="flex justify-end gap-4">
              <button
                className="px-4 py-2 bg-gray-300 rounded-lg"
                onClick={() => setSelectedEvent(null)}
              >
                Zamknij
              </button>
              <button
                className="px-4 py-2 bg-red-500 text-white rounded-lg"
                onClick={handleCancelFirstVisit}
              >
                Anuluj Pierwszą Wizytę
              </button>
            </div>
          </div>
        </div>
      )}
      {modalData && isFirstVisit && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex justify-center items-center p-4 z-50">
          <div className="bg-white p-8 rounded-xl shadow-2xl max-w-md w-full">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">
              Nowy Próbny Trening
            </h2>
            <p className="mb-2">
              Trener:{" "}
              {trainers.find((t) => t._id === modalData.resourceId)?.imie ||
                "Nieznany"}
            </p>
            <p className="mb-2">
              Godzina: {moment(modalData.start).format("HH:mm")} -{" "}
              {moment(modalData.end).format("HH:mm")}
            </p>
            <label className="block mb-2 font-semibold">Imię:</label>
            <input
              type="text"
              className="w-full p-2 border rounded-lg mb-4"
              placeholder="Wpisz imię..."
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
            <label className="block mb-2 font-semibold">Nazwisko:</label>
            <input
              type="text"
              className="w-full p-2 border rounded-lg mb-4"
              placeholder="Wpisz nazwisko..."
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
            <label className="block mb-2 font-semibold">Email:</label>
            <input
              type="email"
              className="w-full p-2 border rounded-lg mb-4"
              placeholder="Wpisz email..."
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <label className="block mb-2 font-semibold">Telefon:</label>
            <input
              type="tel"
              className="w-full p-2 border rounded-lg mb-4"
              placeholder="Wpisz telefon..."
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            <label className="block mb-2 font-semibold">Typ Treningu:</label>
            <select
              className="w-full p-2 border rounded-lg mb-4"
              value={trainingType}
              onChange={(e) => setTrainingType(e.target.value)}
            >
              <option value="">-- Wybierz typ treningu --</option>
              <option value="Konsultacja + trening personalny EMS + drenaż limfatyczny za 69 zł">
                Konsultacja + trening personalny EMS + drenaż limfatyczny za 69
                zł
              </option>
              <option value="Konsultacja + trening personalny siłowy (bez kombinezonu EMS) za 69 zł">
                Konsultacja + trening personalny siłowy (bez kombinezonu EMS) za
                69 zł
              </option>
              <option value="Pierwsza wizyta Program Hybrydowy (EMS + siłowy) za 89 zł">
                Pierwsza wizyta Program Hybrydowy (EMS + siłowy) za 89 zł
              </option>
            </select>
            <div className="flex justify-end gap-4 mt-6">
              <button
                className="px-6 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition duration-200"
                onClick={() => {
                  setIsFirstVisit(false);
                  setModalData(null);
                }}
              >
                Anuluj
              </button>
              <button
                className="px-6 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition duration-200"
                onClick={handleSaveFirstVisit}
              >
                Zapisz Pierwszą Wizytę
              </button>
            </div>
          </div>
        </div>
      )}
      {selectedBreak && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h2 className="text-lg font-bold mb-4">Szczegóły Przerwy</h2>
            <p className="mb-2">Notatka: {selectedBreak.note}</p>
            <div className="flex justify-end gap-4">
              <button
                className="px-4 py-2 bg-gray-300 rounded-lg"
                onClick={() => setSelectedBreak(null)}
              >
                Zamknij
              </button>
              <button
                className="px-4 py-2 bg-red-500 text-white rounded-lg"
                onClick={handleCancelBreak}
              >
                Anuluj Przerwę
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCalendar;
